import { SkeletonPaymentsPage } from '@/components/ui/Skeleton';
import { useToast } from '@/hooks/useToast';
import { API_BASE_URL, api } from '@/lib/api';
import { formatTimeUntilAutoCancel, formatTimeUntilBusinessDeadline } from '@/lib/business-hours';
import { formatCurrency, formatDate, formatSeatIdentifier } from '@/lib/utils';
import { useAppSelector } from '@/store/hooks';
import type { PurchaseWithTickets } from '@eventflow/shared-types';
import { 
  Ban, 
  Bell, 
  Paperclip, 
  Trash2,
  AlertTriangle,
  Smartphone,
  Mail,
  MessageCircle,
  FileDown,
  GraduationCap,
  Lightbulb,
  Clock,
  CheckCircle
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function PendingPaymentsPage() {
  // const navigate = useNavigate(); // No usado actualmente
  const { success: showSuccess, error: showError } = useToast();
  const { organizer } = useAppSelector((state) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedPurchaseRef = useRef<HTMLTableRowElement>(null);

  const [purchases, setPurchases] = useState<PurchaseWithTickets[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPurchase, setSelectedPurchase] = useState<PurchaseWithTickets | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showRejectConfirmedModal, setShowRejectConfirmedModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showDeletePendingModal, setShowDeletePendingModal] = useState(false);
  const [purchaseToDelete, setPurchaseToDelete] = useState<PurchaseWithTickets | null>(null);
  const [filter, setFilter] = useState<
    'all' | 'pending' | 'pending_with_proof' | 'pending_without_proof' | 'confirmed' | 'failed'
  >('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortField, setSortField] = useState<keyof PurchaseWithTickets>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [bankDetails, setBankDetails] = useState<{
    bankName: string | null;
    accountNumber: string | null;
    accountNumberFormatted: string | null;
    accountAlias: string | null;
    accountHolder: string | null;
    frontendUrl: string;
  } | null>(null);

  type DeadlineInfo = {
    label: string;
    rule: string;
    isExpired: boolean;
    isExpiringSoon: boolean;
    badgeClass: string;
    icon: string;
    hoursRemaining: number;
  };

  // Eliminado: etiqueta de "Actualizado" para no mostrar timestamp en UI

  const getDeadlineInfo = (purchase: PurchaseWithTickets): DeadlineInfo | null => {
    if (purchase.payment_status !== 'pending') return null;

    const seatCount = purchase.tickets.length;
    const deadlineHours = purchase.event?.payment_confirmation_deadline_hours;

    // Si el evento tiene "Sin límite" (null), no mostrar información de tiempo límite
    if (deadlineHours === null) {
      return null;
    }

    // Si hay límite personalizado, usarlo; sino usar el predeterminado
    const timeInfo =
      deadlineHours !== undefined && deadlineHours > 0
        ? formatTimeUntilBusinessDeadline(purchase.created_at, deadlineHours)
        : formatTimeUntilAutoCancel(purchase.created_at, seatCount);

    const hasCustomDeadline = Boolean(deadlineHours !== undefined && deadlineHours > 0);
    const seatDescription = `${seatCount} asiento${seatCount !== 1 ? 's' : ''}`;

    // Requisito: eliminar el texto "Límite del evento: 120h hábiles" de la tabla.
    // Para eventos con límite personalizado, no mostraremos regla descriptiva en la lista.
    const limitDescription = hasCustomDeadline
      ? ''
      : `Auto-cancelación: ${timeInfo.limitDays} día${timeInfo.limitDays !== 1 ? 's' : ''} hábil${
          timeInfo.limitDays !== 1 ? 'es' : ''
        } (${timeInfo.limitHours}h) • ${seatDescription}`;

    let badgeClass = 'bg-blue-50 text-blue-700 border-blue-200';
    let icon = 'clock';

    if (timeInfo.isExpired) {
      badgeClass = 'bg-red-50 text-red-700 border-red-200';
      icon = 'alert';
    } else if (timeInfo.hours <= 24) {
      badgeClass = 'bg-red-100 text-red-700 border-red-200';
      icon = 'flame';
    } else if (timeInfo.hours <= 48) {
      badgeClass = 'bg-yellow-50 text-yellow-700 border-yellow-200';
      icon = 'timer';
    }

    return {
      label: timeInfo.isExpired ? 'Tiempo agotado' : timeInfo.formatted,
      rule: limitDescription,
      isExpired: timeInfo.isExpired,
      isExpiringSoon: !timeInfo.isExpired && timeInfo.hours <= 24,
      badgeClass,
      icon,
      hoursRemaining: timeInfo.hours,
    };
  };

  useEffect(() => {
    fetchPurchases();
    fetchBankDetails();
  }, []);

  // Leer parámetros de búsqueda de la URL (desde GlobalSearch)
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    const highlightId = searchParams.get('highlight');

    if (searchFromUrl) {
      setSearchQuery(searchFromUrl);
      setFilter('all'); // Mostrar todos para encontrar el resultado

      // Limpiar parámetros de URL después de usarlos
      setSearchParams({}, { replace: true });
    }

    // Scroll al elemento destacado después de que carguen los datos
    if (highlightId && highlightedPurchaseRef.current) {
      setTimeout(() => {
        highlightedPurchaseRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 500);
    }
  }, [searchParams, setSearchParams]);

  const fetchPurchases = async () => {
    try {
      setIsLoading(true);
      const data = await api.get<PurchaseWithTickets[]>('/purchases/organizer');
      setPurchases(data);
    } catch (err) {
      console.error('Error fetching purchases:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBankDetails = async () => {
    try {
      const data = await api.get<{
        bankName: string | null;
        accountNumber: string | null;
        accountNumberFormatted: string | null;
        accountAlias: string | null;
        accountHolder: string | null;
        frontendUrl: string;
      }>('/config/bank-details');
      setBankDetails(data);
    } catch (err) {
      console.error('Error fetching bank details:', err);
      // Fallback: usar valores por defecto
      setBankDetails({
        bankName: null,
        accountNumber: null,
        accountNumberFormatted: null,
        accountAlias: null,
        accountHolder: null,
        frontendUrl: window.location.origin,
      });
    }
  };

  const handleConfirmPayment = async (purchaseId: string) => {
    if (!confirm('¿Confirmar que recibiste el pago?')) return;

    setIsConfirming(true);
    try {
      await api.post(`/purchases/${purchaseId}/confirm-payment`, {
        paymentStatus: 'confirmed',
      });

      // Actualizar lista
      setPurchases((prev) =>
        prev.map((p) => (p.id === purchaseId ? { ...p, payment_status: 'confirmed' as const } : p))
      );

      setSelectedPurchase(null);
      showSuccess('¡Pago confirmado! El cliente será notificado.');
    } catch (err) {
      console.error('Error confirming payment:', err);
      showError(
        'Error al confirmar pago: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsConfirming(false);
    }
  };

  const handleRejectPayment = async (purchaseId: string) => {
    const reason = prompt('¿Por qué rechazas este pago? (opcional)');
    if (reason === null) return; // Cancelled

    setIsConfirming(true);
    try {
      await api.post(`/purchases/${purchaseId}/confirm-payment`, {
        paymentStatus: 'failed',
        notes: reason || undefined,
      });

      // Actualizar lista con las notas
      setPurchases((prev) =>
        prev.map((p) =>
          p.id === purchaseId
            ? {
                ...p,
                payment_status: 'failed' as const,
                notes: reason || undefined,
              }
            : p
        )
      );

      setSelectedPurchase(null);
      showSuccess('Pago rechazado. Los asientos serán liberados.');
    } catch (err) {
      console.error('Error rejecting payment:', err);
      showError(
        'Error al rechazar pago: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDeletePurchase = async (purchaseId: string) => {
    setIsConfirming(true);
    try {
      await api.delete(`/purchases/${purchaseId}`);

      // Remover de la lista
      setPurchases((prev) => prev.filter((p) => p.id !== purchaseId));

      if (selectedPurchase?.id === purchaseId) {
        setSelectedPurchase(null);
      }

      setShowDeletePendingModal(false);
      setPurchaseToDelete(null);
      showSuccess('Compra eliminada exitosamente. Los asientos han sido liberados.');
    } catch (err) {
      console.error('Error deleting purchase:', err);
      showError(
        'Error al eliminar compra: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsConfirming(false);
    }
  };

  // Verificar si el usuario es el creador del evento
  const isEventCreator = (purchase: PurchaseWithTickets): boolean => {
    if (!organizer) return false;
    return purchase.event.organizer_id === organizer.id;
  };

  // Rechazar un pago confirmado (solo creador del evento)
  const handleRejectConfirmedPayment = async (purchaseId: string) => {
    setIsConfirming(true);
    try {
      await api.post(`/purchases/${purchaseId}/reject-confirmed`, {
        reason: rejectReason || undefined,
      });

      // Actualizar lista con las notas
      setPurchases((prev) =>
        prev.map((p) =>
          p.id === purchaseId
            ? {
                ...p,
                payment_status: 'failed' as const,
                notes: rejectReason
                  ? `Rechazado por el creador del evento: ${rejectReason}`
                  : 'Rechazado por el creador del evento',
              }
            : p
        )
      );

      // Actualizar el purchase seleccionado si es el mismo
      if (selectedPurchase?.id === purchaseId) {
        setSelectedPurchase({
          ...selectedPurchase,
          payment_status: 'failed' as const,
          notes: rejectReason
            ? `Rechazado por el creador del evento: ${rejectReason}`
            : 'Rechazado por el creador del evento',
        });
      }

      setShowRejectConfirmedModal(false);
      setRejectReason('');
      showSuccess(
        'Pago confirmado rechazado. Los asientos han sido liberados. Comunícate con el cliente manualmente.'
      );
    } catch (err) {
      console.error('Error rejecting confirmed payment:', err);
      showError(
        'Error al rechazar pago confirmado: ' +
          (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsConfirming(false);
    }
  };

  // Filtrar, buscar y ordenar compras
  const filteredPurchases = useMemo(() => {
    let result = [...purchases];

    // Aplicar filtro de estado
    if (filter !== 'all') {
      if (filter === 'pending_with_proof') {
        result = result.filter(
          (p) => p.payment_status === 'pending' && Boolean(p.payment_proof_url)
        );
      } else if (filter === 'pending_without_proof') {
        result = result.filter(
          (p) => p.payment_status === 'pending' && !Boolean(p.payment_proof_url)
        );
      } else {
        result = result.filter((p) => p.payment_status === filter);
      }
    }

    // Aplicar búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((p) => {
        const customerName = p.customer_name?.toLowerCase() || '';
        const customerEmail = p.customer_email?.toLowerCase() || '';
        const eventName = p.event.name?.toLowerCase() || '';
        const purchaseId = p.id.toLowerCase();
        const seatIdentifiers = p.tickets
          .map((t) => t.seat.seat_identifier.toLowerCase())
          .join(' ');

        return (
          customerName.includes(query) ||
          customerEmail.includes(query) ||
          eventName.includes(query) ||
          purchaseId.includes(query) ||
          seatIdentifiers.includes(query)
        );
      });
    }

    // Aplicar ordenamiento
    result.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Manejo especial para campos anidados o arrays
      if (sortField === 'event') {
        aValue = a.event.name.toLowerCase();
        bValue = b.event.name.toLowerCase();
      } else if (sortField === 'customer_name' || sortField === 'customer_email') {
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
      } else if (sortField === 'total_amount') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      } else if (sortField === 'created_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortField === 'payment_status') {
        // Ordenar primero por estado (pending/confirmed/failed), luego por si tiene comprobante
        const statusOrder = { pending: 0, confirmed: 1, failed: 2 };
        const aStatusOrder = statusOrder[a.payment_status as keyof typeof statusOrder] ?? 999;
        const bStatusOrder = statusOrder[b.payment_status as keyof typeof statusOrder] ?? 999;

        if (aStatusOrder !== bStatusOrder) {
          aValue = aStatusOrder;
          bValue = bStatusOrder;
        } else {
          // Si mismo estado, ordenar por comprobante (Con comprobante primero)
          aValue = Boolean(a.payment_proof_url) ? 0 : 1;
          bValue = Boolean(b.payment_proof_url) ? 0 : 1;
        }
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [purchases, filter, searchQuery, sortField, sortDirection]);

  // Paginación
  const totalPages = Math.ceil(filteredPurchases.length / pageSize);
  const paginatedPurchases = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredPurchases.slice(startIndex, endIndex);
  }, [filteredPurchases, currentPage, pageSize]);

  // Resetear a página 1 cuando cambia el filtro, búsqueda u ordenamiento
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery, sortField, sortDirection]);

  const handleSort = (field: keyof PurchaseWithTickets) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  /**
   * Genera la sección de datos bancarios para el email (mismo formato que WhatsApp)
   * Usa exactamente la misma lógica que generateWhatsAppMessage
   */
  const generateBankDetailsSection = (): string => {
    // Construir sección de datos bancarios en el mismo orden que WhatsApp
    let bankSection = '';
    if (
      bankDetails?.bankName ||
      bankDetails?.accountNumber ||
      bankDetails?.accountNumberFormatted ||
      bankDetails?.accountAlias ||
      bankDetails?.accountHolder
    ) {
      bankSection = 'DATOS BANCARIOS:\n';

      // Orden específico: Banco, Titular, Número de Cuenta, Número de Transferencia, Alias
      // Exactamente igual que en WhatsApp (sin asteriscos que son para formato WhatsApp)
      if (bankDetails.bankName) {
        bankSection += `Banco: ${bankDetails.bankName}\n`;
      }
      if (bankDetails.accountHolder) {
        bankSection += `Titular: ${bankDetails.accountHolder}\n`;
      }
      if (bankDetails.accountNumberFormatted) {
        bankSection += `Número de Cuenta: ${bankDetails.accountNumberFormatted}\n`;
      } else if (bankDetails.accountNumber) {
        bankSection += `Número de Cuenta: ${bankDetails.accountNumber}\n`;
      }
      if (bankDetails.accountNumber) {
        bankSection += `Número de Transferencia: ${bankDetails.accountNumber}\n`;
      }
      if (bankDetails.accountAlias) {
        bankSection += `Alias: ${bankDetails.accountAlias}\n`;
      }

      bankSection += '\n';
    } else {
      bankSection = 'Para completar tu reserva, realiza una transferencia bancaria.\n\n';
    }

    return bankSection;
  };

  // Generar link de mailto para confirmación de pago
  const generateEmailLink = (
    purchase: PurchaseWithTickets,
    type: 'confirmation' | 'payment_confirmed'
  ) => {
    const seats = purchase.tickets
      .map((t) => formatSeatIdentifier(t.seat.seat_identifier))
      .join(', ');
    const total = formatCurrency(purchase.total_amount);
    const eventDate = formatDate(purchase.event.event_date);
    const reference = purchase.id.substring(0, 8).toUpperCase();
    const bankSection = generateBankDetailsSection();

    let subject = '';
    let body = '';

    if (type === 'confirmation') {
      subject = `Confirmación de Compra - ${purchase.event.name}`;
      body = `Hola ${purchase.customer_name},

¡Gracias por tu compra!

DETALLES DE TU COMPRA:
━━━━━━━━━━━━━━━━━━━━━
Evento: ${purchase.event.name}
Fecha: ${eventDate}
Asientos: ${seats}
Total: ${total}

INSTRUCCIONES DE PAGO:
━━━━━━━━━━━━━━━━━━━━━
${bankSection}IMPORTANTE: Incluye esta referencia en tu transferencia:
${reference}

SIGUIENTES PASOS:
1. Realiza la transferencia bancaria
2. Guarda tu comprobante
3. Súbelo en: ${window.location.origin}/compra/${purchase.id}

Recibirás tus tickets una vez confirmemos tu pago (24 hrs).

¡Nos vemos en el evento!
EventFlow`;
    } else {
      subject = `Pago Confirmado - ${purchase.event.name}`;
      const ticketDownloadUrl = `${API_BASE_URL}/purchases/${purchase.id}/tickets`;
      body = `Hola ${purchase.customer_name},

¡TU PAGO HA SIDO CONFIRMADO!

DETALLES DEL EVENTO:
━━━━━━━━━━━━━━━━━━━━━
Evento: ${purchase.event.name}
Fecha: ${eventDate}
Lugar: ${purchase.event.venue_name}
Asientos: ${seats}
Total Pagado: ${total}

DESCARGA TUS TICKETS (PDF):
━━━━━━━━━━━━━━━━━━━━━
Link directo: ${ticketDownloadUrl}

También disponibles en: ${window.location.origin}/compra/${purchase.id}

INFORMACIÓN IMPORTANTE:
• Presenta tus tickets impresos o en tu móvil
• Lleva identificación oficial
• Llega 30 minutos antes
• Los tickets no son reembolsables

¡Gracias por tu compra! Nos vemos en el evento.
EventFlow`;
    }

    return `mailto:${purchase.customer_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  /**
   * Formatea un número de teléfono para WhatsApp
   * Soporta múltiples formatos y códigos de país
   */
  const formatPhoneForWhatsApp = (phone: string | null | undefined): string | null => {
    if (!phone) return null;

    // Los teléfonos ya están normalizados en la BD (10 dígitos, solo números)
    // Remover todos los caracteres no numéricos
    const digitsOnly = phone.replace(/\D/g, '');

    // Si tiene exactamente 10 dígitos, agregar código de país mexicano (+52)
    if (digitsOnly.length === 10) {
      return `52${digitsOnly}`;
    }

    // Si ya tiene código de país (12 dígitos con 52)
    if (digitsOnly.length === 12 && digitsOnly.startsWith('52')) {
      return digitsOnly;
    }

    // Si no es válido, retornar null
    return null;
  };

  /**
   * Genera el mensaje de WhatsApp sin codificar
   * Útil para copiar al portapapeles
   */
  const generateWhatsAppMessage = (
    purchase: PurchaseWithTickets,
    type: 'confirmation' | 'payment_confirmed'
  ): string => {
    const seats = purchase.tickets
      .map((t) => formatSeatIdentifier(t.seat.seat_identifier))
      .join(', ');
    const total = formatCurrency(purchase.total_amount);
    const eventDate = formatDate(purchase.event.event_date);
    const frontendUrl = bankDetails?.frontendUrl || window.location.origin;
    const purchaseUrl = `${frontendUrl}/compra/${purchase.id}`;
    const reference = purchase.id.substring(0, 8).toUpperCase();

    let message = '';

    if (type === 'confirmation') {
      // Construir sección de datos bancarios en el orden especificado
      // Usar emojis más compatibles con WhatsApp
      let bankSection = '';
      if (
        bankDetails?.bankName ||
        bankDetails?.accountNumber ||
        bankDetails?.accountNumberFormatted ||
        bankDetails?.accountAlias ||
        bankDetails?.accountHolder
      ) {
        bankSection = '\n\n*DATOS BANCARIOS:*\n';

        // Orden específico: Banco, Titular, Número de Cuenta, Número de Transferencia, Alias
        if (bankDetails.bankName) {
          bankSection += `Banco: ${bankDetails.bankName}\n`;
        }
        if (bankDetails.accountHolder) {
          bankSection += `Titular: ${bankDetails.accountHolder}\n`;
        }
        if (bankDetails.accountNumberFormatted) {
          bankSection += `Número de Cuenta: *${bankDetails.accountNumberFormatted}*\n`;
        } else if (bankDetails.accountNumber) {
          bankSection += `Número de Cuenta: *${bankDetails.accountNumber}*\n`;
        }
        if (bankDetails.accountNumber) {
          bankSection += `Número de Transferencia: *${bankDetails.accountNumber}*\n`;
        }
        if (bankDetails.accountAlias) {
          bankSection += `Alias: *${bankDetails.accountAlias}*\n`;
        }

        bankSection += '\n*IMPORTANTE:* Incluye esta referencia en tu transferencia:\n';
        bankSection += `*${reference}*`;
      } else {
        bankSection = '\n\n*INSTRUCCIONES DE PAGO:*\n';
        bankSection += 'Para completar tu reserva, realiza una transferencia bancaria.\n\n';
        bankSection += '*IMPORTANTE:* Incluye esta referencia:\n';
        bankSection += `*${reference}*`;
      }

      message = `Hola *${purchase.customer_name}*

¡Gracias por tu compra!

*DETALLES DE TU COMPRA*
━━━━━━━━━━━━━━━━━━━━━
Evento: ${purchase.event.name}
Fecha: ${eventDate}
Asientos: ${seats}
Total: ${total}${bankSection}

*SIGUIENTES PASOS:*
1. Realiza la transferencia
2. Guarda tu comprobante
3. Súbelo aquí: ${purchaseUrl}

Recibirás tus tickets una vez confirmemos tu pago.`;
    } else {
      const ticketDownloadUrl = `${API_BASE_URL}/purchases/${purchase.id}/tickets`;
      message = `Hola *${purchase.customer_name}*

*¡TU PAGO HA SIDO CONFIRMADO!*

*DETALLES DEL EVENTO*
━━━━━━━━━━━━━━━━━━━━━
Evento: ${purchase.event.name}
Fecha: ${eventDate}
Lugar: ${purchase.event.venue_name || 'Por definir'}
Asientos: ${seats}
Total Pagado: ${total}

*DESCARGA TUS TICKETS (PDF)*
━━━━━━━━━━━━━━━━━━━━━
Link directo: ${ticketDownloadUrl}

También disponibles en: ${purchaseUrl}

*INFORMACIÓN IMPORTANTE:*
- Presenta tickets impresos o en móvil
- Lleva identificación oficial
- Llega 30 minutos antes
- Tickets no reembolsables

¡Nos vemos en el evento!`;
    }

    return message;
  };

  // Generar link de WhatsApp
  const generateWhatsAppLink = (
    purchase: PurchaseWithTickets,
    type: 'confirmation' | 'payment_confirmed'
  ) => {
    // Generar el mensaje usando la función helper
    const message = generateWhatsAppMessage(purchase, type);

    // Codificar el mensaje correctamente para WhatsApp
    // encodeURIComponent maneja UTF-8 correctamente y preserva los emojis
    // Los emojis se codifican como UTF-8 en la URL
    const encodedMessage = encodeURIComponent(message);

    // Si hay teléfono, usar link directo. Si no, usar link web de WhatsApp
    const formattedPhone = formatPhoneForWhatsApp(purchase.customer_phone);

    if (formattedPhone) {
      // Usar wa.me con el número y mensaje codificado
      // WhatsApp Web requiere que el usuario haga clic en "Abrir aplicación"
      // Nota: Si los emojis no se renderizan, puede ser un problema de WhatsApp Web
      // En dispositivos móviles generalmente funciona mejor
      return `https://wa.me/${formattedPhone}?text=${encodedMessage}`;
    }

    // Si no hay teléfono, crear link genérico de WhatsApp
    return `https://wa.me/?text=${encodedMessage}`;
  };

  /**
   * Abre WhatsApp con el mensaje de confirmación pre-cargado
   */
  const handleWhatsAppConfirmation = (purchase: PurchaseWithTickets) => {
    const whatsappLink = generateWhatsAppLink(purchase, 'confirmation');
    window.open(whatsappLink, '_blank');
  };

  /**
   * Abre WhatsApp con el mensaje de pago confirmado pre-cargado
   */
  const handleWhatsAppPaymentConfirmed = (purchase: PurchaseWithTickets) => {
    const whatsappLink = generateWhatsAppLink(purchase, 'payment_confirmed');
    window.open(whatsappLink, '_blank');
  };

  const pendingCount = purchases.filter((p) => p.payment_status === 'pending').length;
  const pendingWithProofCount = purchases.filter(
    (p) => p.payment_status === 'pending' && Boolean(p.payment_proof_url)
  ).length;
  const pendingWithoutProofCount = purchases.filter(
    (p) => p.payment_status === 'pending' && !Boolean(p.payment_proof_url)
  ).length;
  const failedCount = purchases.filter((p) => p.payment_status === 'failed').length;

  if (isLoading) {
    return <SkeletonPaymentsPage />;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Pagos Pendientes</h1>
        <div className="flex flex-wrap items-center gap-2 text-sm sm:text-base text-gray-600">
          <span>Gestiona y confirma los pagos de tus eventos</span>
          {pendingCount > 0 && (
            <span className="inline-flex items-center whitespace-nowrap px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
              {pendingCount} pendiente{pendingCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Filters and Search */}
      <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4">
        {/* Filtros principales */}
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Todas ({purchases.length})
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base font-medium transition-colors ${
                filter === 'pending' ||
                filter === 'pending_with_proof' ||
                filter === 'pending_without_proof'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="hidden sm:inline">Pendientes</span>
              <span className="sm:hidden">Pend.</span> ({pendingCount})
            </button>
            <button
              onClick={() => setFilter('confirmed')}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base font-medium transition-colors ${
                filter === 'confirmed'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="hidden sm:inline">Confirmadas</span>
              <span className="sm:hidden">Conf.</span> (
              {purchases.filter((p) => p.payment_status === 'confirmed').length})
            </button>
            <button
              onClick={() => setFilter('failed')}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base font-medium transition-colors ${
                filter === 'failed'
                  ? 'bg-red-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="hidden sm:inline">Rechazados</span>
              <span className="sm:hidden">Rech.</span> ({failedCount})
            </button>
          </div>

          {/* Subfiltros para pendientes */}
          {(filter === 'pending' ||
            filter === 'pending_with_proof' ||
            filter === 'pending_without_proof') && (
            <div className="ml-2 sm:ml-6 pl-2 sm:pl-4 border-l-2 border-yellow-300 flex flex-wrap gap-1.5 sm:gap-2">
              <button
                onClick={() => setFilter('pending_with_proof')}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors shadow-sm ${
                  filter === 'pending_with_proof'
                    ? 'bg-green-500 text-white border-2 border-green-600'
                    : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                }`}
              >
                <Paperclip className="w-3.5 h-3.5" />{' '}
                <span className="hidden sm:inline">Con comprobante</span> ({pendingWithProofCount})
              </button>
              <button
                onClick={() => setFilter('pending_without_proof')}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors shadow-sm ${
                  filter === 'pending_without_proof'
                    ? 'bg-orange-500 text-white border-2 border-orange-600'
                    : 'bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100'
                }`}
              >
                <Ban className="w-3.5 h-3.5" />{' '}
                <span className="hidden sm:inline">Sin comprobante</span> (
                {pendingWithoutProofCount})
              </button>
              <button
                onClick={() => setFilter('pending')}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors shadow-sm ${
                  filter === 'pending'
                    ? 'bg-yellow-500 text-white border-2 border-yellow-600'
                    : 'bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100'
                }`}
                title="Ver todos los pendientes"
              >
                <span className="hidden sm:inline">Todos los pendientes</span>
                <span className="sm:hidden">Todos</span>
              </button>
            </div>
          )}
        </div>

        {/* Buscador */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Buscar cliente, evento, ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-9 sm:pl-10 pr-9 sm:pr-10 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              aria-label="Limpiar búsqueda"
            >
              <svg
                className="h-5 w-5 text-gray-400 hover:text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Purchases List */}
      {filteredPurchases.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {searchQuery ? 'No se encontraron resultados' : 'No hay compras'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchQuery
              ? `No hay compras que coincidan con "${searchQuery}"`
              : 'No hay resultados para el filtro seleccionado'}
          </p>
        </div>
      ) : (
        <>
          {/* Lista móvil (cards) */}
          <div className="sm:hidden grid gap-4">
            {paginatedPurchases.map((purchase) => {
              const deadlineInfo = getDeadlineInfo(purchase);
              return (
                <div
                  key={purchase.id}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-semibold text-gray-900 leading-tight truncate">
                        {purchase.event.name}
                      </div>
                      <div className="text-xs text-gray-500">{formatDate(purchase.created_at)}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {purchase.payment_status === 'pending' && (
                        <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Pendiente
                        </span>
                      )}
                      {purchase.payment_status === 'confirmed' && (
                        <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-green-100 text-green-800">
                          Confirmado
                        </span>
                      )}
                      {purchase.payment_status === 'failed' && (
                        <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-red-100 text-red-800">
                          Rechazado
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <div className="text-gray-500">Cliente</div>
                      <div className="text-gray-900 truncate">{purchase.customer_name}</div>
                      <div className="text-gray-500 truncate text-xs">
                        {purchase.customer_email}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-500">Total</div>
                      <div className="text-gray-900 font-semibold">
                        {formatCurrency(purchase.total_amount)}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {purchase.tickets.length} asiento
                        {purchase.tickets.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  {deadlineInfo && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-2 px-2 py-1 text-[11px] font-semibold rounded-full border ${deadlineInfo.badgeClass}`}
                      >
                        <span>{deadlineInfo.icon}</span>
                        <span>{deadlineInfo.label}</span>
                      </span>
                      {deadlineInfo.isExpiringSoon && !deadlineInfo.isExpired && (
                        <span className="text-[11px] font-semibold text-red-600 flex items-center gap-1">
                          <Bell className="w-3 h-3" /> Menos de 24 horas
                        </span>
                      )}
                      {deadlineInfo.rule && (
                        <span className="text-[11px] text-gray-600 block w-full">
                          {deadlineInfo.rule}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="mt-3 flex items-center justify-end gap-3">
                    <button
                      onClick={() => setSelectedPurchase(purchase)}
                      className="text-blue-600 hover:text-blue-900 text-sm"
                    >
                      Ver Detalles
                    </button>
                    {purchase.payment_status === 'pending' && isEventCreator(purchase) && (
                      <button
                        onClick={() => {
                          setPurchaseToDelete(purchase);
                          setShowDeletePendingModal(true);
                        }}
                        disabled={isConfirming}
                        className="text-red-600 hover:text-red-900 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        title="Eliminar compra pendiente (solo creador del evento)"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Eliminar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Vista de tabla (>= sm) con scroll horizontal */}
          <div className="hidden sm:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      Fecha
                      {sortField === 'created_at' && (
                        <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('event')}
                  >
                    <div className="flex items-center gap-1">
                      Evento
                      {sortField === 'event' && (
                        <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('customer_name')}
                  >
                    <div className="flex items-center gap-1">
                      Cliente
                      {sortField === 'customer_name' && (
                        <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Asientos
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('total_amount')}
                  >
                    <div className="flex items-center gap-1">
                      Total
                      {sortField === 'total_amount' && (
                        <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('payment_status')}
                  >
                    <div className="flex items-center gap-1">
                      Estado
                      {sortField === 'payment_status' && (
                        <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tiempo límite
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedPurchases.map((purchase) => {
                  const deadlineInfo = getDeadlineInfo(purchase);
                  const hasProof = Boolean(purchase.payment_proof_url);
                  const isPending = purchase.payment_status === 'pending';
                  const rowAccentClasses = ['hover:bg-gray-50'];

                  if (isPending) {
                    if (deadlineInfo?.isExpired) {
                      rowAccentClasses.push('bg-red-50');
                    } else if (deadlineInfo?.isExpiringSoon) {
                      rowAccentClasses.push('bg-orange-50');
                    } else if (hasProof) {
                      rowAccentClasses.push('bg-green-50');
                    } else {
                      rowAccentClasses.push('bg-yellow-50');
                    }
                  }

                  return (
                    <tr key={purchase.id} className={rowAccentClasses.join(' ')}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(purchase.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {purchase.event.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{purchase.customer_name}</div>
                        <div className="text-sm text-gray-500">{purchase.customer_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {purchase.tickets.length} asiento
                        {purchase.tickets.length !== 1 ? 's' : ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(purchase.total_amount)}
                      </td>
                      <td className="px-6 py-4">
                        {purchase.payment_status === 'pending' ? (
                          <div className="flex flex-col gap-1">
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              Pendiente
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${
                                hasProof
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : 'bg-orange-50 text-orange-700 border-orange-200'
                              }`}
                              title={
                                hasProof
                                  ? 'El cliente ya subió un comprobante para este pago'
                                  : 'Aún no se ha subido un comprobante para este pago'
                              }
                            >
                              <div className="flex items-center gap-1">
                                {hasProof ? (
                                  <>
                                    <Paperclip className="w-3 h-3" />
                                    <span>Con comprobante</span>
                                  </>
                                ) : (
                                  <>
                                    <Ban className="w-3 h-3" />
                                    <span>Sin comprobante</span>
                                  </>
                                )}
                              </div>
                            </span>
                          </div>
                        ) : null}
                        {purchase.payment_status === 'confirmed' && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Confirmado
                          </span>
                        )}
                        {purchase.payment_status === 'failed' && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                            Rechazado
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm align-top">
                        {deadlineInfo ? (
                          <div className="flex flex-col gap-1">
                            <span
                              className={`inline-flex items-center gap-2 px-2 py-1 text-xs font-semibold rounded-full border ${deadlineInfo.badgeClass}`}
                              title={
                                deadlineInfo.isExpired
                                  ? 'El límite de confirmación ya venció'
                                  : `Tiempo restante aproximado: ${deadlineInfo.hoursRemaining} hora${
                                      deadlineInfo.hoursRemaining !== 1 ? 's' : ''
                                    }`
                              }
                            >
                              <span>{deadlineInfo.icon}</span>
                              <span>{deadlineInfo.label}</span>
                            </span>
                            {deadlineInfo.isExpiringSoon && !deadlineInfo.isExpired && (
                              <span className="flex items-center gap-1 text-[11px] font-semibold text-red-600">
                                <div className="flex items-center gap-1">
                                  <Bell className="w-3 h-3" />
                                  <span>Menos de 24 horas para expirar</span>
                                </div>
                              </span>
                            )}
                            {deadlineInfo.rule && (
                              <span className="text-[11px] text-gray-600">{deadlineInfo.rule}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedPurchase(purchase)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Ver Detalles
                          </button>
                          {purchase.payment_status === 'pending' && isEventCreator(purchase) && (
                            <button
                              onClick={() => {
                                setPurchaseToDelete(purchase);
                                setShowDeletePendingModal(true);
                              }}
                              disabled={isConfirming}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Eliminar compra pendiente (solo creador del evento)"
                            >
                              <div className="flex items-center gap-1">
                                <Trash2 className="w-4 h-4" />
                                <span>Eliminar</span>
                              </div>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Paginación */}
            {filteredPurchases.length > 0 && totalPages > 0 && (
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                {/* Información de registros */}
                <div className="text-sm text-gray-700">
                  Mostrando{' '}
                  <span className="font-medium">
                    {(currentPage - 1) * pageSize + 1} -{' '}
                    {Math.min(currentPage * pageSize, filteredPurchases.length)}
                  </span>{' '}
                  de <span className="font-medium">{filteredPurchases.length}</span> resultados
                </div>

                {/* Controles de paginación */}
                <div className="flex items-center gap-4">
                  {/* Selector de tamaño de página */}
                  <div className="flex items-center gap-2">
                    <label htmlFor="pageSize" className="text-sm text-gray-700">
                      Por página:
                    </label>
                    <select
                      id="pageSize"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  {/* Botones de navegación */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 disabled:hover:bg-white"
                    >
                      ««
                    </button>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 disabled:hover:bg-white"
                    >
                      ‹
                    </button>
                    <span className="px-3 py-1 text-sm text-gray-700">
                      Página {currentPage} de {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 disabled:hover:bg-white"
                    >
                      ›
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 disabled:hover:bg-white"
                    >
                      »»
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Paginación móvil */}
          {filteredPurchases.length > 0 && totalPages > 0 && (
            <div className="sm:hidden bg-gray-50 px-4 py-3 border-t border-gray-200 flex flex-col items-center justify-center gap-3 mt-4 rounded-lg">
              <div className="text-sm text-gray-700">
                Mostrando{' '}
                <span className="font-medium">
                  {(currentPage - 1) * pageSize + 1} -{' '}
                  {Math.min(currentPage * pageSize, filteredPurchases.length)}
                </span>{' '}
                de <span className="font-medium">{filteredPurchases.length}</span> resultados
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ‹
                </button>
                <span className="px-3 py-1 text-sm text-gray-700">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ›
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »»
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedPurchase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col">
            {/* Header del Modal (Fijo) */}
            <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-start">
              <div>
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Detalles de Compra</h2>
                <p className="text-xs sm:text-sm text-gray-500">
                  ID: {selectedPurchase.id.substring(0, 8)}
                </p>
              </div>
              <button
                onClick={() => setSelectedPurchase(null)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <svg
                  className="h-5 w-5 sm:h-6 sm:w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Contenido del Modal (Scrollable) */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto">
              {/* Cliente */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Cliente</h3>
                <p className="text-sm text-gray-700">{selectedPurchase.customer_name}</p>
                <p className="text-sm text-gray-600">{selectedPurchase.customer_email}</p>
                {selectedPurchase.customer_phone && (
                  <p className="text-sm text-gray-600">{selectedPurchase.customer_phone}</p>
                )}
                {/* Mostrar programa educativo solo para egresados UNACH */}
                {selectedPurchase.customer_type === 'egresado_unach' &&
                  selectedPurchase.graduate_program && (
                    <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="text-xs text-blue-600 font-medium flex items-center gap-1">
                        <GraduationCap className="w-3 h-3" />
                        Egresado UNACH
                      </p>
                      <p className="text-sm text-blue-800">{selectedPurchase.graduate_program}</p>
                    </div>
                  )}
              </div>

              {/* Evento */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Evento</h3>
                <p className="text-sm text-gray-700">{selectedPurchase.event.name}</p>
                <p className="text-sm text-gray-600">
                  {formatDate(selectedPurchase.event.event_date)}
                </p>
              </div>

              {/* Asientos */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Asientos</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedPurchase.tickets.map((ticket) => (
                    <span
                      key={ticket.id}
                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium"
                    >
                      {formatSeatIdentifier(ticket.seat.seat_identifier)}
                    </span>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Total</h3>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(selectedPurchase.total_amount)}
                </p>
              </div>

              {/* Tiempo Restante para Cancelación Automática */}
              {selectedPurchase.payment_status === 'pending' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-orange-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      {(() => {
                        const seatCount = selectedPurchase.tickets.length;
                        const deadlineHours =
                          selectedPurchase.event?.payment_confirmation_deadline_hours;

                        // Si el evento tiene "Sin límite" (null), no mostrar información de tiempo límite
                        if (deadlineHours === null) {
                          return (
                            <>
                              <h3 className="font-semibold text-green-900 mb-1 flex items-center gap-1">
                                <CheckCircle className="w-4 h-4" />
                                Sin límite de tiempo
                              </h3>
                              <p className="text-sm text-green-800">
                                Este evento no tiene límite de tiempo para confirmar pagos. Puedes
                                confirmar o rechazar este pago en cualquier momento.
                              </p>
                            </>
                          );
                        }

                        // Si hay límite, mostrar información de tiempo
                        const timeInfo =
                          deadlineHours !== undefined && deadlineHours > 0
                            ? formatTimeUntilBusinessDeadline(
                                selectedPurchase.created_at,
                                deadlineHours
                              )
                            : formatTimeUntilAutoCancel(selectedPurchase.created_at, seatCount);

                        return (
                          <>
                            <h3 className="font-semibold text-orange-900 mb-1 flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              Tiempo límite de confirmación de pago
                            </h3>
                            <p className="text-sm text-orange-800 mb-2">
                              {deadlineHours !== undefined && deadlineHours > 0 ? (
                                <>
                                  Debes confirmar este pago dentro de{' '}
                                  <strong className="font-bold">{timeInfo.formatted}</strong>.
                                </>
                              ) : (
                                <>
                                  Este pago será cancelado automáticamente si no es confirmado
                                  dentro de{' '}
                                  <strong className="font-bold">{timeInfo.formatted}</strong>.
                                </>
                              )}
                              <br />
                              {deadlineHours !== undefined && deadlineHours > 0 ? (
                                <span className="text-xs text-orange-700">
                                  (Límite: {timeInfo.limitHours} hora
                                  {timeInfo.limitHours !== 1 ? 's' : ''} hábil
                                  {timeInfo.limitHours !== 1 ? 'es' : ''} establecido para este
                                  evento)
                                </span>
                              ) : (
                                <span className="text-xs text-orange-700">
                                  (Límite: {timeInfo.limitDays} día
                                  {timeInfo.limitDays !== 1 ? 's' : ''} hábil
                                  {timeInfo.limitDays !== 1 ? 'es' : ''} - {seatCount} asiento
                                  {seatCount !== 1 ? 's' : ''})
                                </span>
                              )}
                            </p>
                            {timeInfo.isExpired ? (
                              <p className="text-sm font-bold text-red-600 flex items-center gap-1">
                                {deadlineHours !== undefined && deadlineHours > 0 ? (
                                  <>
                                    <AlertTriangle className="w-4 h-4" />
                                    El tiempo para confirmar ha expirado.
                                  </>
                                ) : (
                                  <>
                                    <AlertTriangle className="w-4 h-4" />
                                    El tiempo ha expirado. Este pago será cancelado automáticamente en breve.
                                  </>
                                )}
                              </p>
                            ) : deadlineHours !== undefined && deadlineHours > 0 ? null : (
                              <p className="text-xs text-orange-700 flex items-center gap-1">
                                <Lightbulb className="w-3 h-3" />
                                Horas hábiles: Lunes a Viernes, 9:00 AM - 6:00 PM (hora México)
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Comprobante */}
              {selectedPurchase.payment_proof_url ? (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Comprobante de Pago</h3>
                  <div className="border border-gray-200 rounded-lg p-2 bg-gray-50 max-h-60 overflow-auto">
                    {selectedPurchase.payment_proof_url.toLowerCase().endsWith('.pdf') ? (
                      <a
                        href={selectedPurchase.payment_proof_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-2 p-4 justify-center"
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Ver Comprobante PDF
                      </a>
                    ) : (
                      <a
                        href={selectedPurchase.payment_proof_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={selectedPurchase.payment_proof_url}
                          alt="Comprobante"
                          className="max-w-full h-auto rounded object-contain"
                        />
                      </a>
                    )}
                  </div>
                  {selectedPurchase.bank_reference && (
                    <p className="text-sm text-gray-500 mt-2">
                      Referencia:{' '}
                      <code className="bg-gray-100 px-1 rounded">
                        {selectedPurchase.bank_reference}
                      </code>
                    </p>
                  )}
                </div>
              ) : (
                <div className="card card-compact bg-yellow-50 border-yellow-200 text-yellow-800">
                  <p className="font-semibold flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    No se ha subido comprobante aún.
                  </p>
                </div>
              )}

              {/* Comunicación Manual */}
              <div className="card card-compact bg-blue-50 border-blue-200">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Comunicación Manual
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Envía notificaciones al cliente usando tu email o WhatsApp:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {selectedPurchase.payment_status === 'pending' && (
                    <>
                      <a
                        href={generateEmailLink(selectedPurchase, 'confirmation')}
                        className="btn-secondary text-center text-sm"
                        title="Abrir cliente de email con mensaje de confirmación"
                      >
                        <div className="flex items-center gap-1 justify-center">
                          <Mail className="w-4 h-4" />
                          Email Confirmación
                        </div>
                      </a>
                      <button
                        type="button"
                        onClick={() => handleWhatsAppConfirmation(selectedPurchase)}
                        className="btn-secondary text-center text-sm bg-green-50 hover:bg-green-100 border-green-300"
                        title="Abrir WhatsApp con mensaje de confirmación pre-cargado"
                      >
                        <div className="flex items-center gap-1 justify-center">
                          <MessageCircle className="w-4 h-4" />
                          WhatsApp Confirmación
                        </div>
                      </button>
                    </>
                  )}
                  {selectedPurchase.payment_status === 'confirmed' && (
                    <>
                      <a
                        href={`${API_BASE_URL}/purchases/${selectedPurchase.id}/tickets`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary text-center text-sm bg-purple-50 hover:bg-purple-100 border-purple-300 col-span-full"
                        title="Descargar PDF de tickets para adjuntar manualmente"
                      >
                        <div className="flex items-center gap-1 justify-center">
                          <FileDown className="w-4 h-4" />
                          Descargar Tickets PDF
                        </div>
                      </a>
                      <a
                        href={generateEmailLink(selectedPurchase, 'payment_confirmed')}
                        className="btn-secondary text-center text-sm"
                        title="Abrir cliente de email con mensaje de pago confirmado (incluye link de descarga)"
                      >
                        <div className="flex items-center gap-1 justify-center">
                          <Mail className="w-4 h-4" />
                          Email Pago OK
                        </div>
                      </a>
                      <button
                        onClick={() => handleWhatsAppPaymentConfirmed(selectedPurchase)}
                        className="btn-secondary text-center text-sm bg-green-50 hover:bg-green-100 border-green-300"
                        title="Abrir WhatsApp con mensaje de pago confirmado pre-cargado"
                      >
                        <div className="flex items-center gap-1 justify-center">
                          <MessageCircle className="w-4 h-4" />
                          WhatsApp Pago OK
                        </div>
                      </button>
                    </>
                  )}
                  {selectedPurchase.payment_status === 'failed' && (
                    <>
                      <a
                        href={`mailto:${selectedPurchase.customer_email}?subject=${encodeURIComponent(`TU COMPRA HA SIDO CANCELADA - ${selectedPurchase.event.name}`)}&body=${encodeURIComponent(`Hola ${selectedPurchase.customer_name},\n\nTU COMPRA HA SIDO CANCELADA\n\nLamentamos informarte que tu compra para el siguiente evento ha sido cancelada:\n\n━━━━━━━━━━━━━━━━━━━━━\nDETALLES DE LA COMPRA CANCELADA:\n━━━━━━━━━━━━━━━━━━━━━\n\nEvento: ${selectedPurchase.event.name}\nFecha del evento: ${formatDate(selectedPurchase.event.event_date)}\nAsientos: ${selectedPurchase.tickets.map((t) => formatSeatIdentifier(t.seat.seat_identifier)).join(', ')}\nMonto: ${formatCurrency(selectedPurchase.total_amount)}\nReferencia: ${selectedPurchase.id.substring(0, 8).toUpperCase()}\n\n${selectedPurchase.notes ? `MOTIVO DE LA CANCELACIÓN:\n${selectedPurchase.notes}\n\n` : ''}Los asientos han sido liberados y están disponibles nuevamente.\n\nSi tienes alguna pregunta o necesitas más información, por favor contáctanos.\n\nDisculpa las molestias ocasionadas.\n\nSaludos,\nEquipo de ${selectedPurchase.event.name}`)}`}
                        className="btn-secondary text-center text-sm bg-red-50 hover:bg-red-100 border-red-300"
                        title="Abrir cliente de email con mensaje de notificación de cancelación"
                      >
                        <div className="flex items-center gap-1 justify-center">
                          <Mail className="w-4 h-4" />
                          Email Cancelación
                        </div>
                      </a>
                      <button
                        onClick={() => {
                          const seats = selectedPurchase.tickets
                            .map((t) => formatSeatIdentifier(t.seat.seat_identifier))
                            .join(', ');
                          const total = formatCurrency(selectedPurchase.total_amount);
                          const eventDate = formatDate(selectedPurchase.event.event_date);
                          const reference = selectedPurchase.id.substring(0, 8).toUpperCase();

                          let message = `Hola ${selectedPurchase.customer_name},\n\n*TU COMPRA HA SIDO CANCELADA*\n\nLamentamos informarte que tu compra para el siguiente evento ha sido cancelada:\n\n━━━━━━━━━━━━━━━━━━━━━\n*DETALLES DE LA COMPRA CANCELADA:*\n━━━━━━━━━━━━━━━━━━━━━\n\n*Evento:* ${selectedPurchase.event.name}\n*Fecha del evento:* ${eventDate}\n*Asientos:* ${seats}\n*Monto:* ${total}\n*Referencia:* ${reference}\n\n`;

                          if (selectedPurchase.notes) {
                            message += `*MOTIVO DE LA CANCELACIÓN:*\n${selectedPurchase.notes}\n\n`;
                          }

                          message += `Los asientos han sido liberados y están disponibles nuevamente.\n\nSi tienes alguna pregunta o necesitas más información, por favor contáctanos.\n\nDisculpa las molestias ocasionadas.\n\nSaludos,\nEquipo de ${selectedPurchase.event.name}`;

                          const encodedMessage = encodeURIComponent(message);
                          const formattedPhone = formatPhoneForWhatsApp(
                            selectedPurchase.customer_phone
                          );
                          const whatsappLink = formattedPhone
                            ? `https://wa.me/${formattedPhone}?text=${encodedMessage}`
                            : `https://wa.me/?text=${encodedMessage}`;
                          window.open(whatsappLink, '_blank');
                        }}
                        className="btn-secondary text-center text-sm bg-green-50 hover:bg-green-100 border-green-300"
                        title="Abrir WhatsApp con mensaje de notificación de cancelación"
                      >
                        <div className="flex items-center gap-1 justify-center">
                          <MessageCircle className="w-4 h-4" />
                          WhatsApp Cancelación
                        </div>
                      </button>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2 flex items-start gap-1">
                  <Lightbulb className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>
                    Tip: Los botones de WhatsApp abren la aplicación con el mensaje pre-cargado
                    listo para enviar. El mensaje incluye todos los detalles de la compra.
                  </span>
                </p>
              </div>
            </div>

            {/* Footer del Modal (Fijo) */}
            <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 mt-auto">
              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
                <button
                  onClick={() => setSelectedPurchase(null)}
                  className="btn-secondary text-sm py-2"
                  disabled={isConfirming}
                >
                  Cerrar
                </button>
                {selectedPurchase.payment_status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleRejectPayment(selectedPurchase.id)}
                      disabled={isConfirming}
                      className="btn-danger text-sm py-2"
                      title={
                        !selectedPurchase.payment_proof_url
                          ? 'Rechazar compra sin comprobante'
                          : 'Rechazar compra'
                      }
                    >
                      ✗ Rechazar
                    </button>
                    <button
                      onClick={() => handleConfirmPayment(selectedPurchase.id)}
                      disabled={isConfirming || !selectedPurchase.payment_proof_url}
                      className="btn-primary bg-green-600 hover:bg-green-700 focus:ring-green-500 text-sm py-2"
                    >
                      {isConfirming ? '...' : '✓ Confirmar Pago'}
                    </button>
                  </>
                )}
                {selectedPurchase.payment_status === 'confirmed' && (
                  <>
                    {isEventCreator(selectedPurchase) && (
                      <button
                        onClick={() => setShowRejectConfirmedModal(true)}
                        disabled={isConfirming}
                        className="btn-danger text-sm py-2 disabled:opacity-50"
                        title="Rechazar pago confirmado"
                      >
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          Rechazar
                        </div>
                      </button>
                    )}
                    <a
                      href={`${API_BASE_URL}/purchases/${selectedPurchase.id}/tickets`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary text-sm py-2 text-center"
                    >
                      <div className="flex items-center gap-1">
                        <FileDown className="w-4 h-4" />
                        Tickets PDF
                      </div>
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación para Rechazar Pago Confirmado */}
      {showRejectConfirmedModal && selectedPurchase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
            {/* Header con estilo de advertencia */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 rounded-t-xl">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <svg
                    className="h-10 w-10 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6" />
                    Rechazar Pago Confirmado
                  </h3>
                  <p className="text-red-100 text-sm">
                    Esta es una acción crítica que no se puede deshacer
                  </p>
                </div>
              </div>
            </div>

            {/* Contenido */}
            <div className="p-6 space-y-4">
              {/* Información de la compra */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-sm font-semibold text-gray-900 mb-2">Detalles de la compra:</p>
                <div className="space-y-1 text-sm text-gray-700">
                  <p>
                    <span className="font-medium">Cliente:</span> {selectedPurchase.customer_name}
                  </p>
                  <p>
                    <span className="font-medium">Evento:</span> {selectedPurchase.event.name}
                  </p>
                  <p>
                    <span className="font-medium">Asientos:</span> {selectedPurchase.tickets.length}{' '}
                    asiento
                    {selectedPurchase.tickets.length !== 1 ? 's' : ''}
                  </p>
                  <p>
                    <span className="font-medium">Total:</span>{' '}
                    {formatCurrency(selectedPurchase.total_amount)}
                  </p>
                </div>
              </div>

              {/* Advertencia */}
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-bold text-red-800 mb-1">
                      Esta acción realizará lo siguiente:
                    </h4>
                    <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                      <li>Liberará los asientos inmediatamente</li>
                      <li>Cambiará el estado del pago a "Rechazado"</li>
                      <li>Deberás comunicarte manualmente con el cliente</li>
                      <li>No se puede deshacer esta acción</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Campo de motivo */}
              <div>
                <label
                  htmlFor="rejectReason"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  Motivo del rechazo (opcional pero recomendado)
                </label>
                <textarea
                  id="rejectReason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ejemplo: Pago duplicado, asientos comprometidos internamente, error en la confirmación..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Este motivo se guardará en el historial de la compra
                </p>
              </div>
            </div>

            {/* Footer con botones */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => {
                  setShowRejectConfirmedModal(false);
                  setRejectReason('');
                }}
                disabled={isConfirming}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleRejectConfirmedPayment(selectedPurchase.id)}
                disabled={isConfirming}
                className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed font-bold shadow-lg hover:shadow-xl transition-shadow"
              >
                {isConfirming ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Procesando...
                  </span>
                ) : (
                  'Sí, Rechazar Pago Confirmado'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación para Eliminar Compra Pendiente */}
      {showDeletePendingModal && purchaseToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
            {/* Header con estilo de advertencia */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 rounded-t-xl">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <svg
                    className="h-10 w-10 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                    <Trash2 className="w-6 h-6" />
                    Eliminar Compra Pendiente
                  </h3>
                  <p className="text-orange-100 text-sm">Esta acción no se puede deshacer</p>
                </div>
              </div>
            </div>

            {/* Contenido */}
            <div className="p-6 space-y-4">
              {/* Información de la compra */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  Detalles de la compra a eliminar:
                </p>
                <div className="space-y-1 text-sm text-gray-700">
                  <p>
                    <span className="font-medium">Cliente:</span> {purchaseToDelete.customer_name}
                  </p>
                  <p>
                    <span className="font-medium">Email:</span> {purchaseToDelete.customer_email}
                  </p>
                  <p>
                    <span className="font-medium">Evento:</span> {purchaseToDelete.event.name}
                  </p>
                  <p>
                    <span className="font-medium">Asientos:</span>{' '}
                    {purchaseToDelete.tickets
                      .map((t) => formatSeatIdentifier(t.seat.seat_identifier))
                      .join(', ')}
                  </p>
                  <p>
                    <span className="font-medium">Total:</span>{' '}
                    {formatCurrency(purchaseToDelete.total_amount)}
                  </p>
                  <p>
                    <span className="font-medium">Fecha de compra:</span>{' '}
                    {formatDate(purchaseToDelete.created_at)}
                  </p>
                </div>
              </div>

              {/* Advertencia */}
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-orange-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-bold text-orange-800 mb-1">
                      Esta acción realizará lo siguiente:
                    </h4>
                    <ul className="text-sm text-orange-700 space-y-1 list-disc list-inside">
                      <li>Eliminará permanentemente la compra del sistema</li>
                      <li>Liberará los asientos para nuevas compras</li>
                      <li>Se perderá todo el historial de esta transacción</li>
                      <li>El cliente no recibirá notificación automática</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Info adicional */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800 flex items-start gap-1">
                  <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <span className="font-semibold">Nota:</span> Solo el creador del evento puede
                    eliminar compras pendientes. Si necesitas rechazar la compra pero mantener el
                    registro, usa la opción "Rechazar" en lugar de eliminar.
                  </span>
                </p>
              </div>
            </div>

            {/* Footer con botones */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => {
                  setShowDeletePendingModal(false);
                  setPurchaseToDelete(null);
                }}
                disabled={isConfirming}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDeletePurchase(purchaseToDelete.id)}
                disabled={isConfirming}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all"
              >
                {isConfirming ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Eliminando...
                  </span>
                ) : (
                  'Sí, Eliminar Compra'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
